// Tiny auth store shared across views. Token lives in localStorage so the
// therapist stays logged in across page loads; every API call sends it as a
// Bearer header (no cookies, so it works identically on localhost and behind
// the nginx proxy on remote-emdr.nl).
import Vue from 'vue';
import axios from 'axios';

const TOKEN_KEY = 'emdr-auth-token';
const EMAIL_KEY = 'emdr-auth-email';

function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (e) {
    // localStorage unavailable - session just won't persist
  }
}

export const authState = Vue.observable({
  token: readStorage(TOKEN_KEY),
  email: readStorage(EMAIL_KEY),
});

// Follow logins/logouts made in other tabs, e.g. a therapist who logs in from
// a new tab while a session is already running in this one.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === TOKEN_KEY) authState.token = event.newValue;
    if (event.key === EMAIL_KEY) authState.email = event.newValue;
  });
}

export function isLoggedIn() {
  return !!authState.token;
}

export function setAuth(token, email) {
  authState.token = token;
  authState.email = email;
  writeStorage(TOKEN_KEY, token);
  writeStorage(EMAIL_KEY, email);
}

export function clearAuth() {
  authState.token = null;
  authState.email = null;
  writeStorage(TOKEN_KEY, null);
  writeStorage(EMAIL_KEY, null);
}

// Axios instance that always carries the auth token and logs the user out
// locally if the server rejects the token (expired/revoked).
export const api = axios.create();

api.interceptors.request.use((config) => {
  if (authState.token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${authState.token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401 && authState.token) {
      clearAuth();
    }
    return Promise.reject(error);
  }
);

// Registration no longer logs the user in: the account must first be
// activated via the emailed link. Returns the server response (which in dev
// mode, without SMTP configured, includes the activation link).
export async function getAuthConfig() {
  const { data } = await api.get('/api/auth/config');
  return data;
}

export async function register(email, password, locale, captchaToken) {
  const { data } = await api.post('/api/auth/register', {
    email, password, locale, captcha_token: captchaToken
  });
  return data;
}

export async function resendActivation(email, password, locale) {
  const { data } = await api.post('/api/auth/resend-activation', { email, password, locale });
  return data;
}

export async function deleteAccount(password) {
  await api.delete('/api/auth/account', { data: { password } });
  clearAuth();
}

export async function login(email, password, captchaToken) {
  const { data } = await api.post('/api/auth/login', {
    email, password, captcha_token: captchaToken
  });
  setAuth(data.token, data.email);
}

export async function logout() {
  try {
    await api.post('/api/auth/logout');
  } catch (e) {
    // Token already invalid server-side - still clear locally
  }
  clearAuth();
}
